import type { FilterCondition, SearchResult } from '../services/api';
import { getMatchingMetadataFieldPaths } from './filterUtils';
import { selectSearchResultMetadata } from './searchMetadata';
import {
  canonicalizePromptSemanticSearchFieldPath,
  decodePromptSemanticSearchFieldInstanceKey,
  decodeVideoLevelNestedFieldName,
  encodePromptSemanticSearchFieldInstanceKey,
  encodePromptSemanticSearchFieldPathSegments,
  PromptSemanticSearchFieldInstanceToken,
} from './promptSemanticFieldPaths';

type FieldMatchEvidence = Pick<
  SearchResult,
  'matched_field_paths' | 'matched_field_instances' | 'field_scores' | 'field_instance_scores'
>;
type PathToken = PromptSemanticSearchFieldInstanceToken;

const isFiniteScore = (score: unknown): score is number => (
  typeof score === 'number' && Number.isFinite(score)
);

const hasPrefix = (path: PathToken[], prefix: PathToken[]): boolean => (
  prefix.length > 0
  && prefix.length <= path.length
  && prefix.every((token, index) => path[index] === token)
);

const fieldTokens = (tokens: PathToken[]): string[] => (
  tokens.filter((token): token is string => typeof token === 'string')
);

const fieldIdentity = (tokens: PathToken[]): string => (
  encodePromptSemanticSearchFieldPathSegments(fieldTokens(tokens))
);

const decodePath = (path: string): PathToken[] | null => {
  const normalizedPath = path.trim();
  if (!normalizedPath) return null;
  const exactTokens = decodePromptSemanticSearchFieldInstanceKey(normalizedPath);
  if (exactTokens) return exactTokens;
  const nestedTokens = decodeVideoLevelNestedFieldName(normalizedPath);
  if (nestedTokens) return nestedTokens;
  // Invalid opaque identities cannot safely be interpreted as legacy field names.
  if (normalizedPath.startsWith('__instance__:') || normalizedPath.startsWith('__nested__:')) return null;

  const tokens: PathToken[] = [];
  const pattern = /([^.[\]]+)|\[(\d+)\]/g;
  let match: RegExpExecArray | null;
  let consumed = '';
  while ((match = pattern.exec(normalizedPath)) !== null) {
    const separator = normalizedPath.slice(consumed.length, match.index);
    if (separator !== '' && separator !== '.') return null;
    if (separator === '.' && tokens.length === 0) return null;
    tokens.push(match[2] !== undefined ? Number(match[2]) : match[1].trim());
    consumed = normalizedPath.slice(0, pattern.lastIndex);
  }
  return consumed === normalizedPath && tokens.length > 0 && tokens.every((token) => token !== '')
    ? tokens
    : null;
};

const decodeFieldPath = (path: string): string[] | null => {
  // Legacy condition paths mark repeated scopes with []; opaque semantic paths
  // retain their exact key names, including literal dots or brackets.
  const normalizedPath = decodeVideoLevelNestedFieldName(path.trim())
    ? path
    : path.replace(/\[\]/g, '');
  if (!decodePath(normalizedPath)) return null;
  const canonicalPath = canonicalizePromptSemanticSearchFieldPath(normalizedPath);
  if (!canonicalPath) return null;
  return decodeVideoLevelNestedFieldName(canonicalPath) || fieldTokens(decodePath(canonicalPath) || []);
};

/** Match backend evidence without spreading an exact repeated-field hit to its siblings. */
export const createSearchFieldMatcher = (
  result?: FieldMatchEvidence | null,
): ((path: string, tokens: PathToken[]) => boolean) => {
  const exactPaths: PathToken[][] = [];
  const exactFieldIdentities = new Set<string>();
  const aggregatePaths: string[][] = [];

  const addExactPath = (
    tokens: PathToken[] | null,
    declaredFieldPath?: string,
    isQueryEvidence = true,
  ): void => {
    if (!tokens?.length) return;
    exactPaths.push(tokens);
    // Filter evidence is additive: it cannot narrow legacy query evidence.
    if (!isQueryEvidence) return;
    exactFieldIdentities.add(fieldIdentity(tokens));
    if (declaredFieldPath) {
      const declaredTokens = decodeFieldPath(declaredFieldPath);
      if (declaredTokens?.length) exactFieldIdentities.add(fieldIdentity(declaredTokens));
    }
  };
  const addAggregatePath = (path: string): void => {
    const tokens = decodeFieldPath(path);
    if (tokens?.length) aggregatePaths.push(tokens);
  };

  Object.entries(result?.field_instance_scores || {}).forEach(([path, score]) => {
    if (isFiniteScore(score)) addExactPath(decodePath(path));
  });
  (result?.matched_field_instances || []).forEach((match) => {
    if (!match) return;
    addExactPath(
      decodePromptSemanticSearchFieldInstanceKey(match.field_instance_key || '')
        || decodePath(match.field_instance_path || '')
        || decodePath(match.field_instance_key || ''),
      match.field_path,
    );
  });
  (result?.matched_field_paths || []).forEach((path) => {
    const tokens = decodePath(path);
    if (tokens?.some((token) => typeof token === 'number')) addExactPath(tokens, undefined, false);
    else addAggregatePath(path);
  });
  Object.entries(result?.field_scores || {}).forEach(([path, score]) => {
    if (isFiniteScore(score)) addAggregatePath(path);
  });

  const fallbackPaths = aggregatePaths.filter((tokens) => !exactFieldIdentities.has(fieldIdentity(tokens)));
  return (path, tokens) => {
    const displayTokens = tokens.length > 0 ? tokens : decodePath(path) || [];
    if (displayTokens.length === 0) return false;
    if (exactPaths.some((matchTokens) => hasPrefix(matchTokens, displayTokens))) return true;
    const displayFieldTokens = fieldTokens(displayTokens);
    return fallbackPaths.some((matchTokens) => hasPrefix(matchTokens, displayFieldTokens));
  };
};

/** Add condition evidence using the same metadata and same-item evaluator as filtering. */
export const withSearchFilterMatches = (
  result: SearchResult,
  filters: FilterCondition[],
): SearchResult => {
  if (filters.length === 0) return result;
  const metadata = selectSearchResultMetadata(result.extracted_metadata, result.metadata);
  const matchedPaths = getMatchingMetadataFieldPaths(metadata, filters);
  if (matchedPaths.size === 0) return result;

  const existingPaths = result.matched_field_paths || [];
  const combinedPaths = new Set(existingPaths);
  const previousPathCount = combinedPaths.size;
  matchedPaths.forEach((path) => {
    const tokens = decodePath(path);
    if (!tokens?.length) return;
    combinedPaths.add(tokens.some((token) => typeof token === 'number')
      ? encodePromptSemanticSearchFieldInstanceKey(tokens)
      : encodePromptSemanticSearchFieldPathSegments(fieldTokens(tokens)));
  });
  return combinedPaths.size === previousPathCount
    ? result
    : { ...result, matched_field_paths: Array.from(combinedPaths) };
};
